import { InteractionStatus } from "@hightouch/lib/llm/schema";
import { CreationSource } from "@hightouch/lib/llm/shared";

import type {
  AmpBoardAgentTurnsQuery,
  AmpBoardAgentWorkQuery,
  LlmChatInteractionsBoolExp,
} from "src/graphql";

type Turn = Pick<
  AmpBoardAgentTurnsQuery["llm_chat_interactions"][number],
  "id" | "llm_chat_session_id" | "status" | "created_at" | "llm_chat_session"
>;

/** A turn running now in a project's or task's session. */
export type WorkingTurn = {
  sessionId: string;
  interactionId: string;
  startedAt: string;
};

export type WorkingTask = Pick<
  AmpBoardAgentWorkQuery["amp_tasks"][number],
  "id" | "title" | "project_id"
>;

export type AgentWork = {
  taskIds: ReadonlySet<string>;
  tasks: readonly WorkingTask[];
  projectIds: ReadonlySet<string>;
  turnByTaskId: ReadonlyMap<string, WorkingTurn>;
};

export const NO_AGENT_WORK: AgentWork = {
  taskIds: new Set(),
  tasks: [],
  projectIds: new Set(),
  turnByTaskId: new Map(),
};

/**
 * A turn still pending after this long counts as abandoned: the worker gives a
 * conversation five minutes, so a turn this old ended without closing its row.
 */
export const AGENT_TURN_STALE_MS = 15 * 60_000;

/** How often the turns query's window moves, and so its cache key. */
export const AGENT_TURNS_SINCE_STEP_MS = 60_000;

/**
 * The oldest start the turns query asks for: one stale window back, rounded to
 * the step. The server drops abandoned turns, since the client's own check
 * reruns only when the response changes.
 */
export function agentTurnsSince(now: number): string {
  const step =
    Math.floor(now / AGENT_TURNS_SINCE_STEP_MS) * AGENT_TURNS_SINCE_STEP_MS;
  return new Date(step - AGENT_TURN_STALE_MS).toISOString();
}

/** The top-level turns of the projects agent, and of the specialists it spawned, that may still be running. */
export function agentTurnsWhere(since: string): LlmChatInteractionsBoolExp {
  const isProjectsSession = {
    creation_source: { _eq: CreationSource.ProjectsSystem },
  };
  return {
    status: { _eq: InteractionStatus.Pending },
    // A tool call's child turn runs inside its parent's session, which already counts.
    triggered_by_tool_call_id: { _is_null: true },
    created_at: { _gte: since },
    llm_chat_session: {
      _or: [isProjectsSession, { parent_session: isProjectsSession }],
    },
  };
}

/**
 * The turns running now, one per session, sorted by session so the list is a
 * stable query key. A session that started a new turn before the old one closed
 * its row runs the newer one. A running subagent's turn also counts for the
 * session that started it, which is the one a task or project holds, when that
 * session has no turn of its own.
 */
export function workingTurns(
  turns: readonly Turn[],
  now: number,
): WorkingTurn[] {
  const bySession = new Map<string, WorkingTurn & { startMs: number }>();
  const byParent = new Map<string, WorkingTurn & { startMs: number }>();
  const keepNewer = (
    map: Map<string, WorkingTurn & { startMs: number }>,
    sessionId: string,
    turn: Turn,
    startMs: number,
  ) => {
    const current = map.get(sessionId);
    if (!current || current.startMs < startMs) {
      map.set(sessionId, {
        sessionId,
        interactionId: turn.id,
        startedAt: turn.created_at,
        startMs,
      });
    }
  };
  for (const turn of turns) {
    const startMs = Date.parse(turn.created_at);
    if (
      turn.status !== InteractionStatus.Pending ||
      !Number.isFinite(startMs) ||
      now - startMs >= AGENT_TURN_STALE_MS
    ) {
      continue;
    }
    keepNewer(bySession, turn.llm_chat_session_id, turn, startMs);
    // ponytail: one level up; a subagent started by another subagent credits only its direct parent.
    const parentId = turn.llm_chat_session?.parentSessionId;
    if (typeof parentId === "string") {
      keepNewer(byParent, parentId, turn, startMs);
    }
  }
  for (const [sessionId, turn] of byParent) {
    if (!bySession.has(sessionId)) {
      bySession.set(sessionId, turn);
    }
  }
  return [...bySession.values()]
    .sort((a, b) =>
      a.sessionId < b.sessionId ? -1 : a.sessionId > b.sessionId ? 1 : 0,
    )
    .map(({ sessionId, interactionId, startedAt }) => ({
      sessionId,
      interactionId,
      startedAt,
    }));
}

/** What the agent is working on, from the tasks and projects whose sessions are running. */
export function toAgentWork(
  turns: readonly WorkingTurn[],
  work: AmpBoardAgentWorkQuery | undefined,
): AgentWork {
  if (turns.length === 0 || !work) return NO_AGENT_WORK;
  const turnBySession = new Map(turns.map((turn) => [turn.sessionId, turn]));
  const turnOf = (row: { llm_chat_session_id: string | null }) =>
    row.llm_chat_session_id === null
      ? undefined
      : turnBySession.get(row.llm_chat_session_id);
  const tasks = work.amp_tasks.filter((task) => turnOf(task));
  const projects = work.amp_projects.filter((project) => turnOf(project));
  if (tasks.length === 0 && projects.length === 0) return NO_AGENT_WORK;
  const turnByTaskId = new Map<string, WorkingTurn>();
  for (const task of tasks) {
    const turn = turnOf(task);
    if (turn) turnByTaskId.set(task.id, turn);
  }
  return {
    taskIds: new Set(tasks.map((task) => task.id)),
    tasks: tasks.map(({ id, title, project_id }) => ({
      id,
      title,
      project_id,
    })),
    projectIds: new Set(projects.map((project) => project.id)),
    turnByTaskId,
  };
}
